import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { classNames } from "../../Components"
import { ISailChange } from "../../Types"
import { Icon, Icons } from "../Icon"
import { LogButtonData, LogButtonSailGroupInfo, LogButtonSailInfo, LogButtonSailStaged } from "./Data"

// The sails block: one toggle button per sail group (mutually exclusive), staged
// locally until confirmed. Pressing an active group clears every active member;
// an empty group either hoists its single candidate or opens a selector. The
// confirm button appears only when the staged plan differs from the current one.
//
// The confirm button sits INSIDE this block, below its buttons: it commits the
// staged sail change and nothing else, whereas every other block in the menu
// acts immediately on click (#1220). Its scope has to be obvious from where it
// sits, so it spans this block and no more.
//
// `initSelecting` / `initStaged` are catalog-only seeds: the UI catalog renders a
// fixture exactly once and cannot script a click, so the open selector and the
// pending-save state each need an entry point (same pattern as `initOpen`).
export function LogButtonSails({ sails, saveSails, close, initSelecting, initStaged }: { sails: LogButtonSailInfo[]; saveSails: (changes: ISailChange[]) => void; close: () => void; initSelecting?: string; initStaged?: LogButtonSailStaged }) {
  const app = GetApp();
  const t = app.getT("LogButton");
  const [staged, setStaged] = useState<LogButtonSailStaged>(initStaged ?? {});
  const [selecting, setSelecting] = useState<string | undefined>(initSelecting);

  const groups = useMemo(() => LogButtonData.sailGroups(sails, staged), [sails, staged]);
  const changes = useMemo(() => LogButtonData.sailChanges(sails, staged), [sails, staged]);

  const pick = useCallback((sailId: string) => {
    setStaged((s) => LogButtonData.toggleSail(sails, s, sailId, true));
    setSelecting(undefined);
  }, [sails]);

  const onGroupClick = useCallback((g: LogButtonSailGroupInfo) => {
    if (g.activeIds.length > 0) {
      setStaged((s) => LogButtonData.disableSails(s, g.activeIds));
      setSelecting(undefined);
    } else if (g.sails.length === 1) {
      pick(g.sails[0].id);
    } else {
      setSelecting((cur) => (cur === g.key ? undefined : g.key));
    }
  }, [pick]);

  const confirm = useCallback(() => {
    if (changes.length > 0) {
      saveSails(changes);
    }
    setStaged({});
    setSelecting(undefined);
    close();
  }, [changes, saveSails, close]);

  return (
    <div className="LogButton-block LogButton-sails-block">
      <span className="LogButton-block-label">{t("sails")}</span>
      <div className="LogButton-block-buttons">
        {groups.map((g) => {
          const active = g.activeId ? g.sails.find((s) => s.id === g.activeId) : undefined;
          // Check if this sail is staged (clicked but not yet confirmed) vs truly active
          const isStaged = active && !active.active && staged[active.id] === true;
          // Active sail -> its own title. Inactive -> the group name only when the
          // group offers more than one sail, otherwise the single sail's title.
          const groupName = g.group ? t(`sail_group_${g.group}`) : g.sails[0].title;
          const label = active?.title ?? (g.sails.length > 1 ? groupName : g.sails[0].title);
          return (
            <span key={g.key} className="LogButton-sail-container">
              <button
                className={classNames(["button LogButton-sail-btn", isStaged ? "staged" : (active ? "on" : "off")])}
                title={label}
                onClick={() => onGroupClick(g)}
              />
              <span className="LogButton-sail-name">{label}</span>
              {selecting === g.key && (
                <div className="LogButton-sail-selector">
                  {g.sails.map((s) => (
                    <button
                      key={s.id}
                      className="button LogButton-sail-option"
                      title={s.title}
                      onClick={() => pick(s.id)}
                    >
                      <span>{s.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </span>
          );
        })}
      </div>
      {changes.length > 0 && (
        <button className="button LogButton-sail-confirm" onClick={confirm}>
          <Icon icon={Icons.Checklist} /> {t("sails_confirm")}
        </button>
      )}
    </div>
  );
}
